<template>
    <div class="flex mt-[42px] mb-10 justify-between">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.account_settings") }}
        </h1>
    </div>

    <SettingsTabs class="mt-[42px]" tab-key="api-tokens">
        <div
            class="mt-[42px] grid-cols-layout pb-5 lg:grid"
            @submit.prevent="createApiToken"
        >
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    {{ $t("vue.create_api_token") }}
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    {{ $t("vue.api_tokens_description") }}
                </p>
            </div>
            <form
                class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
            >
                <div class="flex flex-wrap pb-2 pl-6">
                    <TextInput
                        v-model="v$.form.name.$model"
                        class="my-5 mr-6 w-full sm:w-80"
                        :label="$t('vue.name')"
                        :placeholder="$t('vue.type_in_name')"
                        :error="getError('name')"
                    />
                </div>

                <div class="flex flex-wrap p-5 pl-6">
                    <div class="w-full pb-2">
                        <label
                            role="label"
                            class="relative pr-3 text-sm font-medium leading-normal text-gray-700"
                            >{{ $t("vue.permissions") }}</label
                        >
                    </div>
                    <div class="w-1/2">
                        <CheckboxInput
                            v-model:checked="state.form.read"
                            name="readPermission"
                            :label="$t('vue.read')"
                            size="md"
                            class="pb-5"
                        />
                    </div>
                    <div class="w-1/2">
                        <CheckboxInput
                            v-model:checked="state.form.create"
                            :option="true"
                            name="createPermission"
                            :label="$t('vue.create')"
                            size="md"
                        />
                    </div>
                    <div class="w-1/2">
                        <CheckboxInput
                            v-model:checked="state.form.update"
                            name="updatePermission"
                            :label="$t('vue.update')"
                            size="md"
                        />
                    </div>
                    <div class="w-1/2">
                        <CheckboxInput
                            v-model:checked="state.form.delete"
                            name="deletePermission"
                            :label="$t('vue.delete')"
                            size="md"
                        />
                    </div>
                </div>

                <div class="flex justify-end">
                    <div class="my-3 pr-6">
                        <AtomicButton
                            type="submit"
                            color="yellow"
                            size="sm"
                            :disabled="loading"
                        >
                            {{ $t("vue.create") }}
                        </AtomicButton>
                    </div>
                </div>
            </form>
        </div>

        <div
            v-if="tokens.length > 0"
            class="mt-[44px] grid-cols-layout pb-5 lg:grid"
        >
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    {{ $t("vue.manage_api_tokens") }}
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    {{ $t("vue.delete_tokens_description") }}
                </p>
            </div>
            <form
                class="mx-auto flex w-full flex-wrap items-center rounded-md bg-white py-5 shadow-card lg:mx-0"
            >
                <div
                    v-for="token in tokens"
                    :key="token.id"
                    class="mx-6 mt-3 flex w-full justify-between"
                >
                    <div class="flex items-center">
                        {{ token.name }}
                    </div>
                    <div>
                        <AtomicButton
                            type="button"
                            color="yellow"
                            size="sm"
                            class="mr-2"
                            :disabled="loading"
                            @click="editPermissions(token)"
                        >
                            {{ $t("vue.permissions") }}
                        </AtomicButton>
                        <AtomicButton
                            type="button"
                            color="red"
                            size="sm"
                            :disabled="loading"
                            @click="deleteApiToken(token)"
                        >
                            {{ $t("vue.delete") }}
                        </AtomicButton>
                    </div>
                </div>
            </form>
        </div>

        <UpdateApiTokenPermissions
            v-if="selectedApiToken"
            :api-token="selectedApiToken"
            @close="selectedApiToken = undefined"
        />

        <ApiTokenSecretModal
            v-if="newApiToken"
            :api-token="newApiToken"
            @close="newApiToken = undefined"
        />
    </SettingsTabs>
</template>
<script setup lang="ts">
import route from "ziggy-js";
import useConfirm from "@/composables/useConfirm";
import axios, { AxiosError } from "axios";
import useVuelidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import type { PropType } from "vue";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { useTranslations } from "@/composables/useTranslations";

interface IApiToken {
    id: number;
    name: string;
    abilities: string[];
    tokenable_id: number;
    tokenable_type: string;
    created_at: string;
    updated_at: string;
    last_used_at: string;
}

interface IForm {
    form: {
        name: string;
        create: boolean;
        read: boolean;
        update: boolean;
        delete: boolean;
    };
}

const { t } = useTranslations();
const { show } = useConfirm();

defineProps({
    tokens: {
        type: Array as PropType<IApiToken[]>,
        required: true,
    },
});
const loading = ref(false);
const selectedApiToken = ref<IApiToken | undefined>(undefined);
const newApiToken = ref<string | undefined>(undefined);
const errors = ref<IInputErrors>({});

const state: IForm = reactive({
    form: {
        name: "",
        create: false,
        read: false,
        update: false,
        delete: false,
    },
});

const rules = {
    form: {
        name: {
            required,
            maxLength: maxLength(255),
        },
    },
};

const v$ = useVuelidate(rules, state);

const createApiToken = async () => {
    v$.value.form.$touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        try {
            loading.value = true;
            const response = await axios.post(
                route("settings.api-tokens.store"),
                state.form,
            );
            if (response.status === 200) {
                newApiToken.value = response.data.data.plainTextToken;
                state.form = {
                    name: "",
                    create: false,
                    read: false,
                    update: false,
                    delete: false,
                };
                v$.value.$reset();
                errors.value = {};
                router.reload({ only: ["tokens"] });

                create({
                    title: t("vue.api_token_created_successfully"),
                    type: "success",
                });
            }
        } catch (err) {
            if (err instanceof AxiosError) {
                errors.value = err.response?.data.errors;
            }
        } finally {
            loading.value = false;
        }
    }
};

const deleteApiToken = async (token: { id: number }) => {
    const confirmed = await show({
        header: t("vue.delete_api_token"),
        message: t("vue.are_you_sure_delete_api_token"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (!confirmed) {
        return;
    }

    loading.value = true;
    router.delete(route("settings.api-tokens.destroy", token.id), {
        preserveScroll: true,
        onFinish: () => {
            loading.value = false;
        },
        onSuccess: () => {
            create({
                title: t("vue.api_token_deleted_successfully"),
                type: "success",
            });
        },
        onError: () => {
            create({
                title: t("vue.failed_to_delete_api_token"),
                type: "error",
            });
        },
    });
};

const editPermissions = (token: IApiToken) => {
    selectedApiToken.value = token;
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>

<style></style>
